import { Avatar, Box, Typography } from '@mui/material';
import type { GetCardsCardholderPayload } from 'CDK_API';
import { stringAvatar } from 'app/pages/team/groups/utils';
import { fileAccessService } from 'app/services/fileAccess.service';
import { useCallback, useEffect, useState } from 'react';
import { sentryMask } from 'sentryReplayPrivacy';

// Type for the mapping of user IDs to their profile picture URLs
type UserProfilePictureMap = Record<string, string>;

const getProfilePictureMap = async (
  cardholders: (GetCardsCardholderPayload | null | undefined)[]
): Promise<UserProfilePictureMap> => {
  const pictureByUser = new Map<string, string>();
  for (const cardholder of cardholders) {
    if (cardholder?.id && cardholder.picture && !pictureByUser.has(cardholder.id)) {
      pictureByUser.set(cardholder.id, cardholder.picture);
    }
  }

  // Resolved in parallel; a picture that cannot be resolved falls back to the initials avatar.
  const entries = [...pictureByUser.entries()];
  const results = await Promise.allSettled(
    entries.map(([, picture]) => fileAccessService.getDownloadUrl(picture))
  );
  const userProfilePictureMap: UserProfilePictureMap = {};
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') userProfilePictureMap[entries[index]![0]] = result.value;
  });
  return userProfilePictureMap;
};

function useProfilePictureMap(cardholders: (GetCardsCardholderPayload | null | undefined)[]): {
  userProfilePictureMap: UserProfilePictureMap;
  CardholderComponent: (cardholder: GetCardsCardholderPayload | null | undefined) => JSX.Element | null;
} {
  const [userProfilePictureMap, setUserProfilePictureMap] = useState<UserProfilePictureMap>({});

  useEffect(() => {
    async function mapProfilePictures() {
      setUserProfilePictureMap(await getProfilePictureMap(cardholders));
    }

    mapProfilePictures();
  }, [cardholders]);

  const renderCardholder = useCallback(
    (cardholder: GetCardsCardholderPayload | null | undefined): JSX.Element | null => {
      if (!cardholder) return null;
      const { id, name, email } = cardholder;

      const stringAvatarStyled = stringAvatar(name || email);
      Object.assign(stringAvatarStyled.sx, {
        width: 32,
        height: 32,
        fontSize: '1em',
      });

      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{ maxWidth: 32, maxHeight: 32, fontSize: '1em' }}
            key={id}
            title={name || email}
            {...sentryMask}
            {...(userProfilePictureMap[id]
              ? { src: userProfilePictureMap[id] }
              : {
                  ...stringAvatarStyled,
                })}
          />
          <Typography variant="h4" fontWeight="light" ml={1.5} {...sentryMask}>
            {(name || email)?.toUpperCase()}
          </Typography>
        </Box>
      );
    },
    [userProfilePictureMap]
  );

  return {
    userProfilePictureMap,
    CardholderComponent: renderCardholder,
  };
}

export default useProfilePictureMap;
